import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Droplets, FlaskConical, MessageCircle, Store, ChevronRight, Sparkles, Shield, Truck, CalendarDays, Gift, Coins, ShoppingCart, Loader2, Star } from 'lucide-react';
import { apiRequest, formatPrice } from '../lib/utils';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import SEOHead, { PAGE_SEO } from '../components/SEOHead';
import FeaturedReviews from '../components/FeaturedReviews';
import ReviewsHeroBand from '../components/ReviewsHeroBand';

// Fonction pour vérifier si un produit est nouveau (moins de 30 jours)
const isNewProduct = (createdAt) => {
    if (!createdAt) return false;
    const productDate = new Date(createdAt);
    const now = new Date();
    const diffTime = Math.abs(now - productDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30;
};

export default function HomePage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { refreshCart } = useCart();
    const [recommendedProducts, setRecommendedProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [addingToCart, setAddingToCart] = useState(null);

    // Charger les produits les plus vendus
    useEffect(() => {
        loadBestSellers();
    }, []);

    const loadBestSellers = async () => {
        try {
            const products = await apiRequest('/products/best-sellers?limit=4');
            setRecommendedProducts(products);
        } catch (error) {
            console.error('Error loading best sellers:', error);
            // Fallback: charger les premiers produits du catalogue
            try {
                const allProducts = await apiRequest('/products');
                setRecommendedProducts(allProducts.slice(0, 4));
            } catch (e) {
                console.error('Fallback error:', e);
            }
        } finally {
            setLoadingProducts(false);
        }
    };

    const handleAddToCart = async (product) => {
        if (!user) {
            toast.error(t('shop.loginToAddCart'));
            navigate('/auth');
            return;
        }

        setAddingToCart(product.product_id);
        try {
            await apiRequest('/cart/add', {
                method: 'POST',
                body: JSON.stringify({ product_id: product.product_id, quantity: 1 })
            });
            toast.success(t('shop.productAdded'));
            refreshCart();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setAddingToCart(null);
        }
    };
    
    const features = [
        {
            icon: FlaskConical,
            title: t('home.features.analysis'),
            description: t('home.features.analysisDesc'),
            link: '/analysis'
        },
        {
            icon: MessageCircle,
            title: t('home.features.expert'),
            description: t('home.features.expertDesc'),
            link: '/chat'
        },
        {
            icon: Store,
            title: t('home.features.products'),
            description: t('home.features.productsDesc'),
            link: '/shop'
        },
        {
            icon: CalendarDays,
            title: t('nav.appointments'),
            description: t('home.features.expertDesc'),
            link: '/appointment'
        }
    ];

    const benefits = [
        { icon: Sparkles, title: t('home.features.analysis'), description: t('home.features.analysisDesc') },
        { icon: Shield, title: t('home.features.products'), description: t('home.features.productsDesc') },
        { icon: Truck, title: t('cart.continueShopping'), description: t('checkout.delivery') }
    ];

    return (
        <div className="min-h-screen pt-16" data-testid="home-page">
            {/* SEO for Home Page */}
            <SEOHead 
                title={PAGE_SEO.home.title}
                description={PAGE_SEO.home.description}
                keywords={PAGE_SEO.home.keywords}
                canonicalUrl="https://www.ocapiscine.fr/"
            />
            
            {/* Loyalty Banner */}
            <section className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-white py-3 px-4">
                <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-3 text-center sm:text-left">
                    <div className="flex items-center gap-2">
                        <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center">
                            <Gift className="h-5 w-5" />
                        </div>
                        <div>
                            <p className="font-bold text-lg">{t('loyalty.title')} O.C.A</p>
                            <p className="text-sm opacity-90">
                                <Coins className="h-4 w-4 inline mr-1" />
                                <strong>{t('loyalty.earnRate')}</strong> = {t('loyalty.redeemRate')} !
                            </p>
                        </div>
                    </div>
                    <Link to="/shop" className="sm:ml-4">
                        <Button size="sm" className="bg-white text-orange-600 hover:bg-white/90 rounded-full font-semibold">
                            {t('nav.shop')}
                            <ChevronRight className="ml-1 h-4 w-4" />
                        </Button>
                    </Link>
                </div>
            </section>

            {/* Hero Section */}
            <section className="relative min-h-[85vh] flex items-center overflow-hidden">
                <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?w=1920&q=80)' }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-background/90 via-background/70 to-background" />
                
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <div className="max-w-3xl">
                        <div className="flex items-center gap-2 mb-6">
                            <Droplets className="h-10 w-10 text-primary animate-pulse-glow" />
                            <span className="text-primary font-heading font-semibold">O.C.A Piscine</span>
                        </div>
                        
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                            {t('home.title')}
                        </h1>
                        
                        <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-2xl">
                            {t('home.subtitle')}
                        </p>
                        
                        <div className="flex flex-wrap gap-4">
                            <Link to="/analysis" data-testid="cta-analysis">
                                <Button size="lg" className="rounded-full px-8 font-semibold group">
                                    {t('home.cta')}
                                    <ChevronRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                                </Button>
                            </Link>
                            <Link to="/shop" data-testid="cta-shop">
                                <Button size="lg" variant="secondary" className="rounded-full px-8 font-semibold">
                                    {t('home.viewShop')}
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Reviews Hero Band - 5-star carousel */}
            <ReviewsHeroBand />

            {/* Features Section */}
            <section className="py-20 bg-muted/30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
                            {t('home.everythingForPool')}
                        </h2>
                        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                            {t('home.completePoolSolution')}
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {features.map((feature, index) => (
                            <Link 
                                key={index} 
                                to={feature.link}
                                className="group glass rounded-3xl p-8 hover-lift"
                                data-testid={`feature-card-${index}`}
                            >
                                <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                                    <feature.icon className="h-7 w-7 text-primary" />
                                </div>
                                <h3 className="font-heading text-xl font-semibold mb-3">{feature.title}</h3>
                                <p className="text-muted-foreground">{feature.description}</p>
                                <div className="mt-4 flex items-center text-primary font-medium">
                                    {t('common.seeMore')}
                                    <ChevronRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Recommended Products Section */}
            <section className="py-20" data-testid="recommended-products-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between mb-12">
                        <div>
                            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-2">
                                {t('home.recommendedProducts')}
                            </h2>
                            <p className="text-muted-foreground text-lg">
                                {t('home.recommendedProductsDesc')}
                            </p>
                        </div>
                        <Link to="/shop">
                            <Button variant="outline" className="rounded-full hidden sm:flex">
                                {t('shop.seeAllProducts')}
                                <ChevronRight className="ml-2 h-4 w-4" />
                            </Button>
                        </Link>
                    </div>

                    {loadingProducts ? (
                        <div className="flex justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {recommendedProducts.map((product) => (
                                <Card 
                                    key={product.product_id} 
                                    className="group overflow-hidden hover-lift border-0 shadow-lg"
                                    data-testid={`recommended-product-${product.product_id}`}
                                >
                                    <div className="relative aspect-square overflow-hidden bg-muted">
                                        <img
                                            src={product.image_url}
                                            alt={`${product.name} - Produit piscine ${product.brand || 'O.C.A PRO'}`}
                                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                        />
                                        <Badge className="absolute top-3 left-3 bg-background/80 backdrop-blur text-foreground text-xs">
                                            {product.brand}
                                        </Badge>
                                        
                                        {/* Badge Nouveau */}
                                        {isNewProduct(product.created_at) && (
                                            <Badge 
                                                className="absolute top-3 right-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 shadow-lg animate-pulse"
                                            >
                                                <Sparkles className="h-3 w-3 mr-1" />
                                                {t('shop.new')}
                                            </Badge>
                                        )}
                                    </div>
                                    
                                    <CardContent className="p-4">
                                        <Link to={`/shop/${product.product_id}`}>
                                            <h3 className="font-semibold text-sm mb-1 line-clamp-2 hover:text-primary transition-colors">
                                                {product.name}
                                            </h3>
                                        </Link>
                                        <p className="text-xs text-muted-foreground mb-3 line-clamp-1">
                                            {product.category}
                                        </p>
                                        
                                        <div className="flex items-center justify-between">
                                            <span className="text-lg font-bold text-primary">
                                                {formatPrice(product.price)}
                                            </span>
                                            <Button
                                                size="sm"
                                                className="rounded-full"
                                                onClick={() => handleAddToCart(product)}
                                                disabled={addingToCart === product.product_id}
                                                data-testid={`add-to-cart-${product.product_id}`}
                                            >
                                                {addingToCart === product.product_id ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    <>
                                                        <ShoppingCart className="h-4 w-4 mr-1" />
                                                        {t('shop.add')}
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}

                    {/* Mobile: See all button */}
                    <div className="mt-8 text-center sm:hidden">
                        <Link to="/shop">
                            <Button variant="outline" className="rounded-full">
                                {t('shop.seeAllProducts')}
                                <ChevronRight className="ml-2 h-4 w-4" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Loyalty Program Section */}
            <section className="py-16 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="glass rounded-3xl p-8 md:p-12 border-2 border-amber-200 dark:border-amber-800">
                        <div className="grid md:grid-cols-2 gap-8 items-center">
                            <div>
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="h-14 w-14 rounded-2xl bg-amber-500 flex items-center justify-center">
                                        <Gift className="h-7 w-7 text-white" />
                                    </div>
                                    <div>
                                        <h2 className="font-heading text-2xl sm:text-3xl font-bold">{t('home.loyaltyProgram')}</h2>
                                        <p className="text-amber-600 dark:text-amber-400 font-semibold">{t('home.loyaltyRewards')}</p>
                                    </div>
                                </div>
                                
                                <p className="text-muted-foreground text-lg mb-6">
                                    {t('home.loyaltyDescription')}
                                </p>

                                <Link to="/shop">
                                    <Button size="lg" className="rounded-full bg-amber-500 hover:bg-amber-600 text-white">
                                        <Coins className="mr-2 h-5 w-5" />
                                        {t('home.startEarning')}
                                    </Button>
                                </Link>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-white dark:bg-card rounded-2xl p-6 text-center shadow-lg">
                                    <div className="text-4xl font-bold text-amber-500 mb-2">10€</div>
                                    <p className="text-muted-foreground">{t('home.purchase')}</p>
                                </div>
                                <div className="bg-white dark:bg-card rounded-2xl p-6 text-center shadow-lg">
                                    <div className="text-4xl font-bold text-amber-500 mb-2">=</div>
                                    <p className="text-muted-foreground">&nbsp;</p>
                                </div>
                                <div className="bg-white dark:bg-card rounded-2xl p-6 text-center shadow-lg">
                                    <div className="text-4xl font-bold text-emerald-500 mb-2">1 pt</div>
                                    <p className="text-muted-foreground">{t('home.earned')}</p>
                                </div>
                                <div className="bg-white dark:bg-card rounded-2xl p-6 text-center shadow-lg">
                                    <div className="text-4xl font-bold text-emerald-500 mb-2">= 1€</div>
                                    <p className="text-muted-foreground">{t('home.offered')}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured Site Reviews */}
            <FeaturedReviews />

            {/* Benefits Section */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-3 gap-8">
                        {benefits.map((benefit, index) => (
                            <div key={index} className="flex items-start gap-4" data-testid={`benefit-${index}`}>
                                <div className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                                    <benefit.icon className="h-6 w-6 text-accent" />
                                </div>
                                <div>
                                    <h3 className="font-heading font-semibold mb-1">{benefit.title}</h3>
                                    <p className="text-muted-foreground text-sm">{benefit.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-primary/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
                        {t('home.readyToSimplify')}
                    </h2>
                    <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
                        {t('home.startAnalysis')}
                    </p>
                    <Link to="/analysis" data-testid="cta-bottom">
                        <Button size="lg" className="rounded-full px-10 font-semibold">
                            {t('home.startNow')}
                        </Button>
                    </Link>
                </div>
            </section>
        </div>
    );
}
